import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface SessionWarningModalProps {
  isOpen: boolean
  timeRemaining: number // in milliseconds
  onExtend: () => void
  onLogout: () => void
}

const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  isOpen,
  timeRemaining,
  onExtend,
  onLogout
}) => {
  const [displayTime, setDisplayTime] = useState(timeRemaining)

  useEffect(() => {
    if (!isOpen) return

    setDisplayTime(timeRemaining)
    
    const interval = setInterval(() => {
      setDisplayTime(prev => {
        const newTime = Math.max(0, prev - 1000)
        if (newTime <= 0) {
          clearInterval(interval)
          onLogout()
        }
        return newTime
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isOpen, timeRemaining, onLogout])

  const minutes = Math.ceil(displayTime / 60000)
  const seconds = Math.floor((displayTime % 60000) / 1000)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Session Expiring Soon
          </h2>
          <button
            onClick={onLogout}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Your session will expire in:
          </p>
          <div className="text-center">
            <div className="text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {minutes === 0 ? 'Less than a minute' : `${minutes} minute(s) and ${seconds} second(s)`}
            </p>
          </div>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md p-3 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            To continue working, please extend your session. Otherwise, you will be automatically logged out.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onLogout}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Logout Now
          </button>
          <button
            onClick={onExtend}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Extend Session
          </button>
        </div>
      </div>
    </div>
  )
}

export default SessionWarningModal

